import React, { useEffect, useMemo, useCallback } from 'react';
import { Animated, StyleSheet, TouchableOpacity, View } from 'react-native';
import { PieChart, Pie, Cell } from 'recharts';

import { Text, Tooltip } from '../core-ui';
import {
  MAN_DAYS_COLORS,
  COLOR_ARRAY_BILL,
  TOOLTIP_COLOR,
  UNDER_DEVELOPEMENT_COLORS,
} from '../constants/colors';

type HoverContent = {
  message: string;
};
type ChartData = { key: string; value: number; hoverContent?: HoverContent };
type Props = {
  data: Array<ChartData>;
  type: 'Doughnut' | 'Bar';
  size: number;
  height?: number;
  onPress?: (width: number, index: number) => void;
};

function Chart(props: Props) {
  let underDevelopementBar = [
    {
      value: 58,
    },
    { value: 27 },
    {
      value: 14,
    },
  ];

  let { data, type, size, height, onPress } = props;

  if (type === 'Bar') {
    let dataInfo: Array<number> = [];
    let totalData = 0;
    for (let i of data) {
      totalData += i.value;
      if (i.value > 0) {
        dataInfo.push(i.value);
      }
    }
    let widthAnimation = useMemo(() => new Animated.Value(0), []);

    useEffect(() => {
      Animated.timing(widthAnimation, { toValue: size }).start();

      onPress &&
        onPress(((data && data[0] && data[0].value / totalData) * size) / 2, 0);
    }, [size, data]);

    let renderBar = useCallback(() => {
      let content = data.map(({ value, key, hoverContent }, index, thisArr) => {
        const percentage = ((value / totalData) * 100).toFixed();

        let width = (value / totalData) * size;
        let pointerOffset = width / 2;
        for (let i = 0; i < index; i++) {
          pointerOffset += (thisArr[i].value / totalData) * size;
        }
        let TooltipHeader = (
          <>
            <View
              style={[
                styles.tintColor,
                { backgroundColor: MAN_DAYS_COLORS[index] },
              ]}
            />
            <Text weight="medium" style={styles.tooltipTitle}>
              {key}
            </Text>
          </>
        );
        let leftComponentBorderRadius = index === 0 ? 100 : 0;
        let rightComponentBorderRadius =
          index === data.length - 1 ||
          (dataInfo.length <= 2 && dataInfo.length - 1 === index)
            ? 100
            : 0;

        if (totalData === 0) {
          return (
            <View
              key={key}
              style={{
                flexDirection: 'column',
                width: `${underDevelopementBar[index].value}%`,
                minWidth: Number(percentage) > 0 ? `5%` : 0,
                zIndex: Math.abs(index - 3),
                backgroundColor: UNDER_DEVELOPEMENT_COLORS[index],
                borderTopLeftRadius: leftComponentBorderRadius,
                borderBottomLeftRadius: leftComponentBorderRadius,
                borderTopRightRadius: rightComponentBorderRadius,
                borderBottomRightRadius: rightComponentBorderRadius,
              }}
            />
          );
        }
        return (
          <View
            key={key}
            style={{
              flexDirection: 'column',
              width: `${percentage}%`,
              minWidth: Number(percentage) > 0 ? `5%` : 0,
              zIndex: Math.abs(index - 3),
              backgroundColor: MAN_DAYS_COLORS[index],
              borderTopLeftRadius: leftComponentBorderRadius,
              borderBottomLeftRadius: leftComponentBorderRadius,
              borderTopRightRadius: rightComponentBorderRadius,
              borderBottomRightRadius: rightComponentBorderRadius,
            }}
          >
            {percentage !== '0' && (
              <Tooltip
                header={TooltipHeader}
                content={hoverContent && hoverContent.message}
                offset={{ top: 15, left: 10 }}
              >
                <TouchableOpacity
                  style={{
                    position: 'absolute',
                    width: '100%',
                    height: 20,
                    zIndex: 10,
                  }}
                  onPress={() => {
                    onPress && onPress(pointerOffset, index);
                  }}
                />
                <Text style={styles.percentageText}>{`${percentage}%`}</Text>
              </Tooltip>
            )}
          </View>
        );
      });

      return content;
    }, [data, size]);

    return (
      <Animated.View
        style={{
          width: widthAnimation,
          height: height || 20,
          flexDirection: 'row',
          zIndex: 1,
        }}
      >
        {renderBar()}
      </Animated.View>
    );
  }

  if (type === 'Doughnut') {
    return (
      <PieChart width={size} height={size} style={styles.pieChart}>
        <Pie
          data={data}
          innerRadius={size / 2 - 20}
          outerRadius={size / 2}
          dataKey="value"
          startAngle={90}
          endAngle={-450}
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={COLOR_ARRAY_BILL[index]} />
          ))}
        </Pie>
      </PieChart>
    );
  }
  return null;
}

const styles = StyleSheet.create({
  pieChart: {
    alignContent: 'center',
  },
  tooltipContent: {
    width: 280,
    padding: 12,
    backgroundColor: TOOLTIP_COLOR.background,
    position: 'absolute',
    top: 15,
    left: 10,
    shadowColor: TOOLTIP_COLOR.shadow,
    shadowOpacity: 0.2,
    shadowRadius: 12,
    shadowOffset: {
      height: 4,
      width: 0,
    },
    zIndex: 2,
  },
  tooltipHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  tooltipTitle: {
    flex: 1,
  },
  tintColor: {
    width: 8,
    height: 8,
    borderRadius: 8 / 2,
    marginRight: 8,
  },
  divider: {
    height: 1,
    backgroundColor: TOOLTIP_COLOR.divider,
    marginVertical: 8,
  },
  percentageText: {
    fontSize: 12,
    color: 'white',
    alignSelf: 'center',
    zIndex: 8,
    marginTop: 2,
  },
});

export default Chart;
